import { v7 as uuidv7 } from "uuid";

/**
 * Build a minimal EmailTemplate document that passes the canonical
 * JSON Schema (apps/api/schemas/document.schema.json).
 *
 * Schema gotchas the blank doc has to respect:
 *   - additionalProperties: false everywhere — only ship known fields
 *   - meta.tenant_id is `string format:uuid` if present (server fills),
 *     so we omit it entirely on create
 *   - meta.description is `string` if present; Laravel's
 *     ConvertEmptyStringsToNull middleware turns "" → null which fails
 *     validation, so we omit it
 *   - brand.logo.url is `string format:uri` and required — empty string
 *     is not a valid URI, so we ship a placeholder asset URL the
 *     editor will overwrite when the user uploads a logo
 */
export interface BlankTemplateOptions {
  name: string;
  templateType?:
    | "marketing"
    | "transactional"
    | "newsletter"
    | "welcome_series"
    | "abandoned_cart"
    | "re_engagement"
    | "system";
  language?: string;
}

const PLACEHOLDER_LOGO_URL = "https://emailer.sendpulse.net/placeholder-logo.png";

export function buildBlankTemplate(
  opts: BlankTemplateOptions,
): Record<string, unknown> {
  const language = opts.language ?? "en";
  return {
    schema_version: "1.0.0",
    meta: {
      name: opts.name,
      tags: [],
      template_type: opts.templateType ?? "marketing",
    },
    default_language: language,
    supported_languages: [language],
    theme: {
      colors: {
        primary: "#0066ff",
        primary_contrast: "#ffffff",
        background: "#ffffff",
        surface: "#fafafa",
        text_primary: "#111111",
        text_secondary: "#52525b",
        border: "#e5e7eb",
        accent: "#0066ff",
      },
      typography: {
        font_family_primary: "Inter",
        font_size_base: 14,
        line_height_base: 1.5,
      },
      spacing: { base: 4 },
      border: { radius_md: 4, width_sm: 1 },
      layout: { max_width: 600, section_padding: 20 },
      brand: { logo: { url: PLACEHOLDER_LOGO_URL } },
    },
    variables: [],
    sections: [
      {
        id: uuidv7(),
        type: "section",
        rows: [
          {
            id: uuidv7(),
            type: "row",
            layout: "1-col",
            columns: [
              {
                id: uuidv7(),
                type: "column",
                blocks: [],
              },
            ],
          },
        ],
      },
    ],
  };
}
